import { useFlags } from 'launchdarkly-react-client-sdk';

import { useGetOrganizationMemberships } from 'api/queries/organizations/memberships';
import { useGetCurrentUserOrganizationAccessStatus } from 'api/queries/users/memberships';
import { GroupDto } from 'legacy/groups/types';
import { FLAGS } from 'legacy/common/constants/launchdarkly';

export enum MembershipAccessCode {
  NEED_MEMBERSHIP_ACCESS = 'NEED_MEMBERSHIP_ACCESS'
}

function useMembershipGateCheck({ group }: { group: GroupDto }) {
  const flags = useFlags();

  // For unauthenticated users, don't make any API calls
  const isUserAuthenticated = Boolean(group?.metadata?.currentUser?.role);

  const { data: products, isLoading: isOrgMembershipsProductsLoading } =
    useGetOrganizationMemberships({
      organizationId: group?.id,
      enabled: Boolean(group?.id && isUserAuthenticated)
    });

  const hasProducts = products && products?.length > 0;

  const {
    data,
    error,
    isLoading: isUserMembershipsAccessStatusLoading
  } = useGetCurrentUserOrganizationAccessStatus({
    organizationId: group?.id,
    enabled: Boolean(group?.id && hasProducts && isUserAuthenticated)
  });

  const currentUser = group?.metadata?.currentUser;
  const isUserApproved = currentUser?.status === 'approved';

  let hasOngoingMembership = false;
  // For unauthenticated users or users that are not approved, return default state
  if (!isUserAuthenticated || !isUserApproved) {
    hasOngoingMembership = false;
  } else if (!hasProducts) {
    // If the organization has no membership products, the user has access
    hasOngoingMembership = true;
  } else if (error?.code === MembershipAccessCode.NEED_MEMBERSHIP_ACCESS) {
    // Error case: check for specific membership access errors first
    hasOngoingMembership = false;
  } else if (data?.success && !error) {
    // Success case: user has valid access (only if no error)
    hasOngoingMembership = true;
  } else {
    hasOngoingMembership = false;
  }

  return {
    hasOngoingMembership:
      !flags[FLAGS.ENABLE_ORG_MEMBERSHIPS] || hasOngoingMembership,
    isLoading:
      isUserAuthenticated &&
      (isOrgMembershipsProductsLoading || isUserMembershipsAccessStatusLoading)
  };
}

export default useMembershipGateCheck;
